import React, {useState, useEffect} from 'react';
import {useDebouncedCallback} from 'use-debounce';

import PriceInputs from '../PriceInputs/PriceInputs';
import {
    EDAP_LABEL_NIGHTLY_PRICE_SLIDER,
    EDAP_LABEL_TOTAL_PRICE_SLIDER,
} from '../../utils/constants';
import {
    getLimitedMaxPrice,
    getLimitedMaxDisplayPrice,
} from '../../utils/priceUtils';
import Slider from '../Slider/Slider';

import type {FilterGroupContentProps} from '../FilterGroupContent/FilterGroupContent';

import './PriceInputsWrapper.less';

type Props = Pick<
    FilterGroupContentProps,
    | 'currency'
    | 'priceFilterOptions'
    | 'priceInputValuesAreValid'
    | 'updatePriceInputValuesValidity'
> & {
    appliedMaxPrice: number | null;
    appliedMinPrice: number | null;
    selectedMaxPrice: number | null;
    selectedMinPrice: number | null;
    filterByTotalPrice: boolean;
    onInputsWrapperPriceChange: (filters: any) => void;
};

export type BoundsType = Array<number | null>;

const normalizeBounds = (
    maxAllowedPrice: number,
    [minPrice, maxPrice]: BoundsType
): BoundsType => {
    const normalizedMinPrice = minPrice || 0;
    let normalizedMaxPrice;
    if (maxAllowedPrice && maxPrice && maxPrice >= maxAllowedPrice) {
        normalizedMaxPrice = null;
    } else {
        normalizedMaxPrice = maxPrice;
    }
    return [normalizedMinPrice, normalizedMaxPrice];
};

export default function PriceInputsWrapper({
    appliedMinPrice,
    appliedMaxPrice,
    selectedMinPrice,
    selectedMaxPrice,
    filterByTotalPrice,
    currency,
    onInputsWrapperPriceChange,
    priceFilterOptions = {},
    priceInputValuesAreValid,
    updatePriceInputValuesValidity,
}: Props) {
    const [activeHandleIndex, setActiveHandleIndex] = useState(null);
    const [internalPrice, setInteralPrice] = useState({
        minPrice: appliedMinPrice,
        maxPrice: appliedMaxPrice,
    });

    const edapLabel = filterByTotalPrice
        ? EDAP_LABEL_TOTAL_PRICE_SLIDER
        : EDAP_LABEL_NIGHTLY_PRICE_SLIDER;

    const [debouncedCallback] = useDebouncedCallback(
        onInputsWrapperPriceChange,
        200
    );

    useEffect(() => {
        setInteralPrice({
            minPrice: selectedMinPrice,
            maxPrice: selectedMaxPrice,
        });
    }, [selectedMinPrice, selectedMaxPrice]);

    const minPriceDisplay = internalPrice.minPrice || 0;
    const maxAllowedPrice = getLimitedMaxPrice(
        currency,
        filterByTotalPrice,
        priceFilterOptions.lengthOfStay
    );

    const maxPriceDisplay = getLimitedMaxDisplayPrice(
        maxAllowedPrice,
        internalPrice.maxPrice
    );
    const isMax = maxPriceDisplay >= maxAllowedPrice;

    const handlePriceChange = (bounds: BoundsType) => {
        const [minPrice, maxPrice] = normalizeBounds(maxAllowedPrice, bounds);

        const newPriceValues = {
            minPrice,
            maxPrice,
        };

        setInteralPrice(newPriceValues);
        debouncedCallback(newPriceValues);
    };

    return (
        <React.Fragment>
            <div
                className="PriceInputsWrapper__PriceInputs"
                data-wdio="PriceInputsWrapper__PriceInputs"
            >
                <PriceInputs
                    activeHandleIndex={activeHandleIndex}
                    currency={currency}
                    filterByTotalPrice={filterByTotalPrice}
                    isMax={isMax}
                    maxAllowedPrice={maxAllowedPrice}
                    maxPrice={maxPriceDisplay}
                    minPrice={minPriceDisplay}
                    onChange={handlePriceChange}
                    priceInputValuesAreValid={priceInputValuesAreValid}
                    updatePriceInputValuesValidity={
                        updatePriceInputValuesValidity
                    }
                    useSeparatorBetweenInputs
                />
            </div>
            <div
                className="PriceInputsWrapper__slider"
                data-wdio="PriceInputsWrapper__slider"
            >
                <Slider
                    className={'Slider--min-max-price-fields-test'}
                    start={minPriceDisplay}
                    stop={maxPriceDisplay}
                    max={maxAllowedPrice}
                    activeHandleIndex={activeHandleIndex}
                    step={10}
                    onChange={handlePriceChange}
                    onSetActiveHandle={setActiveHandleIndex}
                    edapLabel={edapLabel}
                />
            </div>
        </React.Fragment>
    );
}
